import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { servicesData } from '../data/services';
import { useLanguage } from '../context/LanguageContext';
import { Button } from "../components/ui/button";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const service = servicesData.find(s => s.slug === slug);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white pt-32 flex flex-col items-center">
        <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
        <Link to="/" className="text-red-500 hover:underline">Return Home</Link>
      </div>
    );
  }

  // Get translations based on the service key
  const title = t.services[`${service.translationKey}_title`];
  const description = t.services[`${service.translationKey}_desc`];

  return (
    <div className="min-h-screen bg-neutral-950 text-white pt-20">
      {/* Hero Section */}
      <div className="relative h-[400px] w-full overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${service.image})` }}
        >
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center">
          <Link to="/" className="inline-flex items-center text-neutral-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Link>
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
            {title}
          </h1>
          <div className="flex items-center gap-2 text-red-500 font-semibold uppercase tracking-wider">
            {service.category} Service
          </div>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-4 text-white">Overview</h2>
              <p className="text-xl text-neutral-400 leading-relaxed">
                {description}
              </p>
              <p className="text-neutral-400 mt-4 leading-relaxed">
                At Paysagiste A Catalogna, we pride ourselves on delivering top-tier {title.toLowerCase()} services. 
                Our team uses only the highest quality materials and latest techniques to ensure durable and beautiful results.
              </p>
            </div>

            <div className="bg-neutral-900 p-8 rounded-xl border border-neutral-800">
              <h3 className="text-xl font-bold mb-6 text-white">Why Choose Us?</h3>
              <ul className="space-y-4">
                {[
                  "Experienced & Professional Team",
                  "High-Quality Materials",
                  "Competitive Pricing",
                  "Satisfaction Guaranteed"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <span className="text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA Sidebar */}
          <div className="space-y-8">
            <div className="bg-black p-8 rounded-xl border border-neutral-800 sticky top-24">
              <h3 className="text-2xl font-bold mb-4 text-white">Get a Quote</h3>
              <p className="text-neutral-400 mb-6">
                Ready to start your {title.toLowerCase()} project? Contact us today for a free consultation.
              </p>
              <Button 
                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Request Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
